<template>
    <section
        id="fast-and-easy"
        class="experience-principles-section section"
        :data-shape-id="shapeId"
        ref="sectionWrapper"
    >
        <div class="wrapper wrapper-medium">
            <div
                class="section-copy"
                :class="[{ 'overlay-open': this.isEpOverlayOpen && this.activeSectionName === sectionName }, visual]"
            >
                <div class="default-copy">
                    <div class="overline">Experience Principle</div>
                    <h2 class="major-heading">
                        {{ headline }}
                    </h2>
                    <p>
                        {{ copy }}
                    </p>
                </div>
                <div class="overlay-copy">
                    <div class="small-heading">What this means for our experiences:</div>
                    <p>
                        {{ overlayCopy }}
                    </p>
                </div>
            </div>
            <div class="section-visual" :class="visual">
                <div class="image-wrapper" :class="visual">
                    <div class="inner-container">
                        <div class="lottie-container">
                            <lottie-animation
                                ref="lottie"
                                v-if="lottieData"
                                :path="`./animation-data/${lottieData}.json`"
                                :loop="lottieLoop"
                            />
                        </div>
                        <div class="button-container">
                            <button-icon
                                :icon="buttonIcon"
                                :class="{ 'transform-to-close': isEpOverlayOpen }"
                                @click.native="toggleOverlay"
                                srText="open overlay test"
                                hasPulseEffect
                            />
                        </div>
                    </div>
                    <img v-if="!lottieData || sectionName === 'ep-secure-and-appreciated'" :src="getImgUrl(visual)" />
                </div>
            </div>
        </div>
        <particles :particles="particles" />
    </section>
</template>

<script>
    import ButtonIcon from '../../patterns/buttons/ButtonIcon.vue';
    import LottieAnimation from './../../LottieAnimation.vue';
    import Particles from '../../Particles.vue';

    export default {
        name: 'ExperiencePrinciplesSection',
        components: { ButtonIcon, LottieAnimation, Particles },
        props: [
            'headline',
            'copy',
            'overlayCopy',
            'shapeId',
            'buttonIcon',
            'visual',
            'sectionName',
            'lottieData',
            'lottieColorChange',
            'lottieLoop',
            'particles',
        ],
        methods: {
            getImgUrl(fileName) {
                return require('../../../assets/svg/devices/' + fileName + '.svg');
            },
            toggleOverlay() {
                this.isEpOverlayOpen ? this.closeOverlay() : this.openOverlay();
            },
            openOverlay() {
                this.$store.dispatch('updateBackgroundShape', 'popup');
                this.$store.dispatch('updateLogoColorScheme', 'inverted');
                this.$store.dispatch('toggleEpOverlay');

                if (this.activeSectionName === 'ep-secure-and-appreciated') {
                    this.$refs.lottie.playLoop();
                } else {
                    this.$refs.lottie.play();
                }

                if (this.lottieColorChange) {
                    this.$refs.lottie.triggerPathFill('#ffffff');
                }
            },
            closeOverlay() {
                this.$store.dispatch('updateBackgroundShape', this.$refs.sectionWrapper.dataset.shapeId);
                this.$store.dispatch('updateLogoColorScheme', 'default');
                this.$store.dispatch('toggleEpOverlay');

                if (this.activeSectionName === 'ep-secure-and-appreciated') {
                    this.$refs.lottie.playReverse();
                } else {
                    this.$refs.lottie.playReverse();
                }

                if (this.lottieColorChange) {
                    this.$refs.lottie.triggerPathFill('#e20074');
                }
            },
            resetLottie() {
                this.$refs.lottie.stop();
                if (this.lottieColorChange) {
                    this.$refs.lottie.triggerPathFill('#e20074');
                }
            },
        },
        computed: {
            isEpOverlayOpen() {
                return this.$store.getters.isEpOverlayOpen;
            },
            activeSectionName() {
                return this.$store.getters.activeSectionName;
            },
        },
    };
</script>

<style scoped lang="scss">
    .experience-principles-section {
        &.active {
            .section-copy {
                animation: anim-fade-in 1s 0.7s forwards;
            }

            .section-visual {
                animation: anim-fade-in-from-bottom 1s 0.9s forwards;
            }
        }

        &.on-leave {
            .section-copy,
            .section-visual {
                animation: anim-fade-out 0.3s forwards;
            }
        }

        .wrapper {
            @include flex($align-items: center);
        }

        .section-copy {
            width: 50%;
            @include border-box();
            z-index: 1;
            position: relative;
            opacity: 0;

            @media (max-width: breakpoint('medium')) {
                width: 100%;
                text-align: center;
                margin-top: spacing(10);
            }

            .overlay-copy {
                color: #ffffff;
                position: absolute;
                top: 0;
                opacity: 0;
                top: 50%;
                transform: translateY(-50%);
            }

            &.overlay-open {
                overflow: hidden;

                .default-copy {
                    opacity: 0;
                }

                .overlay-copy {
                    opacity: 1;
                }

                @keyframes anim-fade-in {
                    from {
                        transform: translateX(-20px);
                        opacity: 0;
                    }

                    to {
                        transform: translateX(0px);
                        opacity: 1;
                    }
                }

                @keyframes anim-fade-in-from-bottom {
                    from {
                        transform: translateY(20px);
                        opacity: 0;
                    }

                    to {
                        transform: translateY(0px);
                        opacity: 1;
                    }
                }

                @keyframes anim-fade-out {
                    from {
                        transform: translateY(0px);
                        opacity: 1;
                    }

                    to {
                        transform: translateY(20px);
                        opacity: 0;
                    }
                }

                @keyframes anim-fade-out-to-bottom {
                    from {
                        transform: translateY(0px);
                        opacity: 1;
                    }

                    to {
                        transform: translateY(20px);
                        opacity: 0;
                    }
                }
            }

            &.speaker {
                @media (min-width: breakpoint('medium') + 1) {
                    p {
                        padding-right: spacing(15);
                    }
                }
            }
        }

        .section-visual {
            width: 50%;
            @include flex($justify-content: flex-end);
            opacity: 0;

            @media (max-width: breakpoint('extra-large')) {
                @include flex($justify-content: center);
            }

            @media (max-width: breakpoint('medium')) {
                width: 100%;
            }

            &.speaker {
                @include flex($justify-content: flex-start);
            }

            .image-wrapper {
                position: relative;

                @media (max-width: breakpoint('medium')) {
                    width: 100%;
                    margin-top: spacing(10);
                }

                @media (max-width: breakpoint('small')) {
                    margin-top: spacing(6);
                }

                img {
                    @include reset-img();
                    position: relative;
                }

                .button-container {
                    position: absolute;
                    z-index: 1;
                }

                .lottie-container {
                    height: 100%;
                }

                &.phone {
                    height: 55vh;

                    @media (max-width: breakpoint('medium')) {
                        height: 35vh;
                    }

                    .inner-container {
                        position: absolute;
                        content: '';
                        top: 0;
                        border-radius: calc(55vh / 28);
                        left: 50%;
                        transform: translateX(-50%);
                        height: 55vh;
                        width: calc(55vh * 0.51);
                        box-shadow: 10px 45px 55px 0px rgba(0, 0, 0, 0.35);

                        @media (max-width: breakpoint('medium')) {
                            height: 35vh;
                            border-radius: calc(35vh / 28);
                            width: calc(35vh * 0.51);
                        }
                    }

                    img {
                        width: auto;
                        height: 100%;
                        margin: 0 auto;
                    }

                    .button-container {
                        bottom: 25%;
                        right: 0;
                        transform: translateX(50%);
                    }
                }

                &.speaker {
                    width: 80%;

                    @media (max-width: breakpoint('medium')) {
                        width: 50%;
                    }

                    @media (max-width: breakpoint('small')) {
                        width: 70%;
                    }

                    .button-container {
                        bottom: 25%;
                        right: 0;
                        transform: translateX(50%);
                    }

                    &:after {
                        position: absolute;
                        content: '';
                        top: 0;
                        width: 95%;
                        right: 2.5%;
                        padding-top: 86%;
                        border-radius: 12%;
                        box-shadow: 10px 45px 55px 0px rgba(0, 0, 0, 0.35);
                        z-index: -1;
                    }

                    .lottie-container {
                        position: absolute;
                        top: 50%;
                        transform: translate(-50%, -50%) scale(2);
                        width: 100%;
                        left: 195%;
                    }
                }

                &.tv {
                    position: absolute;
                    width: 50vw;
                    left: -25%;
                    top: 50%;
                    transform: translate(25%, -50%);

                    @media (max-width: breakpoint('medium')) {
                        position: relative;
                        width: 80%;
                        top: initial;
                        left: initial;
                        transform: initial;
                    }

                    @media (max-width: breakpoint('small')) {
                        width: 100%;
                    }

                    .button-container {
                        bottom: 0.5vw;
                        left: 10%;
                    }

                    &:after {
                        position: absolute;
                        content: '';
                        top: 0;
                        width: 100%;
                        padding-top: 52.5%;
                        border-radius: 2%;
                        box-shadow: 10px 45px 55px 0px rgba(0, 0, 0, 0.35);
                        z-index: -1;
                    }
                }
            }
        }

        @media (max-width: breakpoint('extra-small')) {
            .overline {
                display: none;
            }
        }
    }
</style>
